import { useContext, useEffect, useRef, useState } from "react"
import "./file.less"
import { Spin, Table, message } from "antd"
import classNames from "classnames"
import { TaskType, getTaskTypeName } from "@/declare/enums/task"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { NavigationContext, RegisterContext } from "@/App"
import { ReactComponent as PathSvg } from "./icons/path.svg"
import { ReactComponent as AddSvg } from './icons/add.svg';
import { ReactComponent as RedoSvg } from './icons/redo.svg';
import { ReactComponent as UndoSvg } from './icons/undo.svg';
import { ReactComponent as SearchSvg } from './icons/search.svg';
import FileTable from "./file-index"
import { Input, TabMenuItem, TnModal, ContextMenu, ContextMenuModal, ModelListItem, Dropdown, IconButton } from "bimui"
import { File } from "@/declare/interface/project/file"
import { addFile, deleteFileByUuidList, findFileMapByProjectUuidAndTypes, getFilePathByFileUuid, updateFile } from "@/declare/api/project/file"
import { FileType } from "@/declare/enums/file-type"
import { Major } from "@/declare/interface/project/major"
import { BaseUtils } from "tnbase"
import localStore from "@/store/local-store"
import { findProjectUsers } from "@/declare/api/project/project-user"
import { User } from "@/declare/interface/project/user"
import { emitter } from "@/utils/mitt"
import { Emit_Event_Common_Tool } from "@/declare/enums/emit-event"
import { SIDEBAR_TYPE } from "@/declare/enums/menu-type"

/**
 * 排序类型
 */
const FilterType = {
    NONE: 0,    // 无排序
    ORDER: 1,   // 顺序
    REVERSE: 2, // 倒序
}

interface FileItem {
    level: number,
    file: File
}

export default function FilePage() {

    const { projectUuid } = useParams()
    /**
     * 导航上下文
     */
    const navigateContext = useContext(NavigationContext);
    /**
     * 注册上下文
     */
    const registerContext = useContext(RegisterContext);
    /**
     * 路由
     */
    const location = useLocation();

    /**
     * 文件列表源
     */
    const [originFile, setOriginFile] = useState<File>()

    /**
     * 控制右键菜单的显示状态
     */
    const [contextMenuVisible, setContextMenuVisible] = useState<boolean>(false)

    /**
     * 右键菜单的左侧位置
     */
    const [contextMenuLeft, setContextMenuLeft] = useState<number>(0)

    /**
     * 右键菜单的顶部位置
     */
    const [contextMenuTop, setContextMenuTop] = useState<number>(0)

    /**
     * 激活的文件
     */
    const [activeFile, setActiveFile] = useState<File>(null)

    /**
     * 左侧侧边栏激活编辑的文件条目
     */
    const [editFile, setEditFile] = useState<File>(null)

    /**
     * 专业label列表
     */
    const [majorList, setMajorList] = useState<{ label: string, value: string }[]>([])

    /**
     * 过滤后的专业label列表
     */
    const [displayMajorList, setDisplayMajorList] = useState<{ label: string, value: string }[]>([])

    /**
     * 展示左侧子项的列表
     */
    const [showMenuItemUuidList, setShowMenuItemUuidList] = useState<string[]>(["project"])

    /**
     * 项目参与人列表
     */
    const [users, setUsers] = useState<User[]>([])

    /**
     * 激活的文件的路径
     */
    const [pathFileList, setPathFileList] = useState<File[]>([])

    /**
     * 历史打开的文件回退
     */
    const [historyFileUndo, setHistoryFileUndo] = useState<File[]>([])

    /**
     * 历史打开的文件重回
     */
    const [historyFileRedo, setHistoryFileRedo] = useState<File[]>([])

    /**
     * 搜索文字
     */
    const [searchInput, setSearchInput] = useState<string>("")

    /**
     * 删除事件的uuid
     */
    const [eventDeleteUuid, setEventDeleteUuid] = useState<number>(1); // 事件删除的uuid
    const eventDeleteUuidRef = useRef(eventDeleteUuid);
    /**
     * 编辑事件的uuid
     */
    const [eventEditUuid, setEventEditUuid] = useState<number>(1); // 事件复制的uuid
    const eventEditUuidRef = useRef(eventEditUuid);

    /**
     * 导航中右键选中的条目
     */
    const [rightSelectedFile, setRightSelecetdFile] = useState<File>();
    /**
     * 右键选中的条目的层级
     */
    const [rightSelectedLevel, setRightSelecetdLevel] = useState<number>();
    /**
     * 右侧选中的文件列表
     */
    const [selectedFileUuids, setSelectedFileUuids] = useState<string[]>([]);
    const activeFileRef = useRef<File>(activeFile);

    useEffect(() => {
        emitter.on(Emit_Event_Common_Tool.DELETE, onListenDelete);
        emitter.on(Emit_Event_Common_Tool.EDIT, onListenEdit);

        return () => {
            emitter.off(Emit_Event_Common_Tool.DELETE, onListenDelete);
            emitter.off(Emit_Event_Common_Tool.EDIT, onListenEdit);
            // 离开时记录当前打开的文件，用于下次打开是默认展开
            navigateContext.setCurrentActiveFile(activeFileRef.current);
        };
    }, [])

    // 及时记录activeFile的值
    useEffect(() => {
        activeFileRef.current = activeFile
    }, [activeFile])

    // 监听删除
    const onListenDelete = () => {
        setEventDeleteUuid(eventDeleteUuidRef.current + 1);
    }

    // 监听编辑
    const onListenEdit = () => {
        setEventEditUuid(eventEditUuidRef.current + 1);
    }

    useEffect(() => {
        eventDeleteUuidRef.current = eventDeleteUuid;
        if (selectedFileUuids.length != 0 || eventDeleteUuid == 1) {
            // 右侧有选中的条目，不删除导航中选中的条目
            // 删除uuid为初始值，不删除
            return;
        }
        onClickDelete(activeFile);
    }, [eventDeleteUuid])

    useEffect(() => {
        eventEditUuidRef.current = eventEditUuid;
        onClickEdit();
    }, [eventEditUuid])

    /**
     * 删除某个文件
     */
    const onClickDelete = (file: File) => {
        if (!file) {
            return;
        }
        TnModal().deleteModal({
            content: file.childrenCount ? "是否将下级文件全部删除？" : null,
            onOk() {
                TnModal().destoryAll();
                deleteFileByUuidList([file.uuid]).then(res => {
                    generateData()
                }).catch(err => {
                    message.error("删除失败");
                }).finally(() => {
                    setActiveFile(originFile);
                })
            },
            onCancel() {
                TnModal().destoryAll();
            },
        });
    }
    /**
     * 编辑某个
     */
    const onClickEdit = () => {

    }

    /**
     * 注册页签按钮
     */
    useEffect(() => {
        generateData()
    }, [projectUuid])

    useEffect(() => {
        if (!activeFile) {
            setPathFileList([])
            return
        }
        if (activeFile.uuid == "project") {
            setPathFileList([activeFile])
            return
        }
        getFilePathByFileUuid(activeFile.uuid).then(res => {
            let resFilePath: File[] = res.data || []
            setPathFileList(resFilePath)
            let fileUuids = resFilePath.map(item => item.uuid);
            setShowMenuItemUuidList([
                ...showMenuItemUuidList,
                ...fileUuids,
            ])
        }).catch((e) => {
            message.error("获取文件路由失败！")
        })
    }, [activeFile])

    /**
     * 初始化数据
     */
    const generateData = () => {
        navigateContext.registTab({
            key: location.pathname,
            value: "文件",
        })
        navigateContext.setActiveTabKey(location.pathname)

        generateMajor()

        findFileMapByProjectUuidAndTypes(projectUuid, [FileType.MODEL_PHYSICS]).then(res => {
            let originFileList: File[] = res.data || []
            let projectFile: File = {
                uuid: "project",
                name: "项目",
                childrenCount: originFileList.reduce((acc, item) => { return acc + item.childrenCount }, 0)
            }
            projectFile.children = originFileList
            setOriginFile(projectFile)
            if (navigateContext.currentActiveFile && navigateContext.currentActiveFile.projectUuid == projectUuid) {
                setActiveFile(navigateContext.currentActiveFile)
            } else {
                let activeFile = null;
                if (originFileList.length > 0) {
                    // 选中第一个专业
                    activeFile = originFileList[0]
                }
                // 展开所有专业
                let uuids = originFileList.map(item => item.uuid);
                setShowMenuItemUuidList([
                    ...showMenuItemUuidList,
                    ...uuids,
                ])
                if (activeFile != null) {
                    setActiveFile(activeFile);
                } else {
                    setActiveFile(projectFile)
                }
            }
        })

        findProjectUsers(projectUuid).then(res => {
            if (res.status === 200) {
                let data: User[] = res.data || [];
                setUsers(data);
            }
        }).catch(err => {
            console.log('err', err)
            message.error("获取项目参与人失败");
        })
    }

    /**
     * 只更新数据，不更新其他状态
     */
    const onlyGenerateData = (deleteActive: boolean) => {
        findFileMapByProjectUuidAndTypes(projectUuid, [FileType.MODEL_PHYSICS]).then(res => {
            let originFileList: File[] = res.data || []
            let projectFile: File = {
                uuid: "project",
                name: "项目",
                childrenCount: originFileList.reduce((acc, item) => { return acc + item.childrenCount }, 0)
            }
            projectFile.children = originFileList
            setOriginFile({ ...projectFile })
            if (deleteActive) {
                setActiveFile(projectFile)
            }
        })
    }

    /**
     * 初始化专业列表
     */
    const generateMajor = () => {
        let majorList: Major[] = registerContext.majors;
        let newList: { label: string, value: string }[] = []
        majorList.forEach(m => {
            newList.push({
                label: m.name,
                value: m.uuid
            })
        })
        setMajorList(newList)
    }

    /**
     * 点击添加
     * @param parentFile 
     */
    const onClickLeftAdd = (parentFile: File) => {
        let newFile: File
        if (parentFile.uuid == "project") {
            // 在项目下面新增专业
            let children = parentFile.children || [];
            // 已经存在过的专业
            let existMajors = children.map(item => item.majorUuid);
            // 过滤掉已经选过的专业
            let displayMajorList = majorList.filter(item => !existMajors.includes(item.value));
            setDisplayMajorList(displayMajorList);
            if (displayMajorList.length == 0) {
                message.warning("已无可新增的专业");
                return;
            }
            newFile = {
                uuid: BaseUtils.genUUID(),
                parentUuid: parentFile.uuid,
                name: displayMajorList[0]?.label,
                type: FileType.MODEL_PHYSICS,
                majorUuid: displayMajorList[0]?.value,
                isNew: true
            }
        } else {
            // 在专业下面新增分区模型
            newFile = {
                uuid: BaseUtils.genUUID(),
                parentUuid: parentFile.uuid,
                name: "待命名",
                type: FileType.MODEL_PHYSICS,
                majorUuid: parentFile?.majorUuid,
                isNew: true,
                childrenCount: 1,
            }
        }

        addFile(projectUuid, newFile).then(res => {
            setEditFile(newFile)
            let midOrigin = originFile
            addFileInFile(midOrigin, newFile)
            setOriginFile({ ...midOrigin })

            if (!showMenuItemUuidList.includes(parentFile.uuid)) {
                setShowMenuItemUuidList([
                    ...showMenuItemUuidList,
                    parentFile.uuid
                ])
            }
        })
    }

    /**
     * 往一个model里面添加model，根据addModel的parentUuid来查找需要添加到哪个model
     * @param file 
     * @param addModel 
     * @returns 
     */
    const addFileInFile = (file: File, addModel: File) => {
        if (addModel.parentUuid == file.uuid) {
            if (!file.children) {
                file.children = []
            }
            if (addModel.type == FileType.MODEL_PHYSICS) {
                file.childrenCount += 1;
                addModel.childrenCount = 1;
            }
            file.children.push(addModel)
            return file.childrenCount
        }
        if ((file?.children || []).length > 0) {
            let count = 0;
            file?.children?.forEach(c => {
                let cCount = addFileInFile(c, addModel)
                count += cCount;
            })
            if (file.type == FileType.MODEL_PHYSICS) {
                count++;
            }
            file.childrenCount = count;
            return file.childrenCount;
        } else {
            return file.childrenCount;
        }
    }

    /**
     * 打开文件（便于回退等操作）
     * @param file 
     */
    const openFile = (file: File) => {
        historyFileUndo.push(activeFile)
        setHistoryFileUndo([...historyFileUndo])
        setHistoryFileRedo([])
        setActiveFile(file)
    }

    const updateStatus = (activeFile: any) => {
        const old = localStore.getSidebarStatus(SIDEBAR_TYPE.FILE);
        if (!!old && !!old.activeFile && old.activeFile.uuid === activeFile?.uuid) {
            return;
        }
        // 在这里向外传递状态
        let status = {
            activeFile: activeFile,
        };
        localStore.setSidebarStatus(SIDEBAR_TYPE.FILE, status);
    }

    useEffect(() => {
        const fileStatus = localStore.getSidebarStatus(SIDEBAR_TYPE.FILE);
        if (!fileStatus) return;
        const file = fileStatus.activeFile;
        if (!file) return;
        setActiveFile({ ...file });
    }, [])

    useEffect(() => {
        if (activeFile == null) return;
        updateStatus(activeFile);
    }, [activeFile])

    /**
     * 设置文件展开与收起
     * @param uuid 
     */
    const setItemShow = (uuid: string) => {
        if (showMenuItemUuidList.find(item => item == uuid)) {
            setShowMenuItemUuidList(showMenuItemUuidList.filter(item => item != uuid))
        } else {
            setShowMenuItemUuidList([
                ...showMenuItemUuidList,
                uuid
            ])
        }
    }

    /**
     * 输入框失焦保存
     * @param value 
     * @returns 
     */
    const onBlurConfirmEditInput = (value: string) => {
        if (!editFile) return

        updateFile(editFile.uuid, { name: value }).then(res => {
            editFile.name = value
            let midOrigin = { ...originFile }
            saveFileName(midOrigin, editFile)
            setOriginFile({ ...midOrigin })
        }).catch(e => {
            message.error("更新失败！")
        }).finally(() => {
            setEditFile(null)
        })
    }

    /**
     * 下拉选择保存
     * @param value 
     * @returns 
     */
    const onDropDownConfirmSelect = (value: string) => {
        let majorItem = majorList.find(item => item.value == value)
        if (!majorItem || !editFile) {
            setEditFile(null)
            return
        }
        updateFile(editFile.uuid, { name: majorItem.label, majorUuid: majorItem.value }).then(res => {
            editFile.name = majorItem.label
            editFile.majorUuid = majorItem.value
            editFile.isNew = false
            let midOrigin = { ...originFile }
            saveFileName(midOrigin, editFile)
            setOriginFile({ ...midOrigin })
        }).catch(e => {
            message.error("更新失败！")
        }).finally(() => {
            setEditFile(null)
        })

    }

    /**
     * 保存文件名称
     * @param file 
     * @param addModel 
     * @returns 
     */
    const saveFileName = (file: File, addModel: File) => {
        if (addModel.uuid == file.uuid) {
            file = { ...addModel }
        }
        file?.children?.forEach(c => {
            saveFileName(c, addModel)
        })
        return
    }

    /**
     * 文件条目body
     * @param item 
     * @returns 
     */
    const LeftMenuDetail = (item: FileItem) => {

        return (
            <div className="file-menu-item">
                {editFile && editFile?.uuid == item?.file?.uuid ? (
                    <div className="edit-item" onClick={(e) => e.stopPropagation()}>
                        {(item.level == 1) ? (
                            <Dropdown
                                style={{
                                    height: 24,
                                    marginLeft: 48,
                                    marginRight: 2,
                                }}
                                options={displayMajorList}
                                value={editFile?.majorUuid?.toString()}
                                onChange={(e) => {
                                    onDropDownConfirmSelect(e)
                                }}
                            />
                        ) : (
                            <Input
                                autoFocus
                                style={{
                                    marginLeft: item.level * 20 + 12 + (item?.file?.children?.length > 0 ? 0 : 20),
                                    height: 32,
                                    display: "flex",
                                    flex: "1 1 auto"
                                }}
                                defaultValue={editFile?.name || ""}
                                onBlur={(e) => {
                                    onBlurConfirmEditInput(e.target.value)
                                }}
                                onEnter={(e) => {
                                    let t = e as any
                                    onBlurConfirmEditInput(t.target.value)
                                }}
                            />
                        )}
                    </div>
                ) : (
                    <ModelListItem
                        active={activeFile?.uuid == item?.file?.uuid}
                        name={`${item?.file?.name || ''}${!!item?.file?.childrenCount ? `(${item?.file?.childrenCount})` : ''}`}
                        showSvg={item?.file?.children?.length > 0}
                        style={{
                            paddingLeft: item.level * 20 + 12 + (item?.file?.children?.length > 0 ? 0 : 20)
                        }}
                        onClick={() => {
                            if (!showMenuItemUuidList.includes(item.file?.uuid)) {
                                // 如果没有展开，展开
                                setItemShow(item.file.uuid)
                            }
                            openFile(item.file)
                        }}
                        onDoubleClick={() => {
                            if (item?.file?.uuid == "project") {
                                setEditFile(null)
                            } else {
                                if (item.level == 2) {
                                    // 在编辑专业
                                    // 查询可以选的专业
                                    // 同项目下已经选的专业
                                    let existMajors = originFile.children.map(file => file.majorUuid);
                                    let displayMajorList = majorList.filter(major => !existMajors.includes(major.value) || major.value == item.file.majorUuid);
                                    setDisplayMajorList(displayMajorList);
                                }
                                setEditFile(item?.file)
                            }
                        }}
                        onContextMenu={(e) => {
                            e.preventDefault();
                            if (item?.file?.uuid == "project") return;
                            setContextMenuLeft(e.clientX);
                            setContextMenuTop(e.clientY);
                            setRightSelecetdLevel(item.level);
                            setRightSelecetdFile(item.file);
                            setContextMenuVisible(true)
                        }}
                        showSvgActive={showMenuItemUuidList.includes(item.file?.uuid)}
                        onClickShowSvg={() => {
                            setItemShow(item.file.uuid)
                        }}
                    >
                        <div className='button-item' onClick={(e) => {
                            e.stopPropagation()
                            onClickLeftAdd(item.file)
                        }}>
                            <AddSvg />
                        </div>
                    </ModelListItem>
                )}
                {item.file?.children?.length > 0 && showMenuItemUuidList.includes(item.file.uuid) && (
                    <div className="children">
                        {item.file.children.map((childrenItem) => (
                            <LeftMenuDetail
                                key={childrenItem.uuid}
                                file={childrenItem}
                                level={item.level + 1}
                            />
                        ))}
                    </div>
                )}
            </div>
        )
    }

    /**
     * 回退文件打开
     */
    const undoOpenFile = () => {
        if (historyFileUndo.length > 0) {
            let file = historyFileUndo[historyFileUndo.length - 1]
            historyFileUndo.pop()
            historyFileRedo.push(file)
            setHistoryFileUndo([...historyFileUndo])
            setHistoryFileRedo([...historyFileRedo])
            setActiveFile(file)
        }
    }

    /**
     * 重新文件打开
     */
    const redoOpenFile = () => {
        if (historyFileRedo.length > 0) {
            let file = historyFileRedo[historyFileRedo.length - 1]
            historyFileRedo.pop()
            historyFileUndo.push(file)
            setHistoryFileUndo([...historyFileUndo])
            setHistoryFileRedo([...historyFileRedo])
            setActiveFile(file)
        }
    }

    /**
     * 点击了页面
     */
    const onClickPage = () => {
        if (editFile) {
            setEditFile(null);
        }
    }

    return (
        <div className="file-page-main" onClick={onClickPage}>
            <div className="file-page-head">
                <IconButton style={{ height: 24, width: 24 }} icon={<UndoSvg style={{ height: 16, width: 16 }} />} onClick={() => { undoOpenFile() }} />
                <IconButton style={{ height: 24, width: 24 }} icon={<RedoSvg style={{ height: 16, width: 16 }} />} onClick={() => { redoOpenFile() }} />
                <div className="head-path">
                    {pathFileList?.map(((item, index) => (
                        <>
                            {index != 0 && <PathSvg className="path-svg" />}
                            <div className="path-name" onClick={() => {
                                openFile(item)
                            }}>
                                {item.name}
                            </div>
                        </>
                    )))}
                </div>
                {/* todo 搜索框组件 */}
                <Input
                    prefix={<SearchSvg style={{ marginRight: 4, marginLeft: 8 }} />}
                    value={searchInput}
                    style={{ width: 340 }}
                    onChange={(e) => {
                        setSearchInput(e.target.value)
                    }} />
            </div>
            <div className="page-main">
                <div className="left-main">
                    <LeftMenuDetail key={originFile?.uuid} file={originFile} level={0} />
                </div>
                <div className="right-main">
                    <FileTable
                        openFile={openFile}
                        searchValue={searchInput}
                        generateParentData={onlyGenerateData}
                        activeFile={activeFile}
                        users={users}
                        searchInput={""}
                        setActiveFile={setActiveFile}
                        onChangeSelected={(uuids) => setSelectedFileUuids(uuids)}
                    />
                </div>
            </div>


            <ContextMenuModal
                visible={contextMenuVisible}
                left={contextMenuLeft}
                top={contextMenuTop}
                wrapperClassname='file-file-context-menu'
                onCancel={() => { setContextMenuVisible(false) }}
            >
                <div className="file-menu-item" onClick={() => {
                    if (!rightSelectedFile) return;
                    if (rightSelectedLevel == 2) {
                        // 在编辑专业
                        // 查询可以选的专业
                        // 同项目下已经选的专业
                        let existMajors = originFile.children.map(file => file.majorUuid);
                        let displayMajorList = majorList.filter(major => !existMajors.includes(major.value) || major.value == rightSelectedFile.majorUuid);
                        setDisplayMajorList(displayMajorList);
                    }
                    setEditFile(rightSelectedFile)
                    setContextMenuVisible(false);
                }}>
                    {rightSelectedLevel == 1 ? '改专业' : '重命名'}
                </div>
                {/* <div className="file-menu-item">
                    移动到
                </div> */}
                <div className="file-menu-item file-menu-item-delete" onClick={() => {
                    onClickDelete(rightSelectedFile)
                    setContextMenuVisible(false);
                }}>
                    删除
                </div>

            </ContextMenuModal>
        </div>
    )
}